const DB_NAME = 'dms_document_cache';
const DB_VERSION = 1;
const STORE_NAME = 'documents';
const MAX_CACHE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'docId' });
      }
    };

    request.onsuccess = (event) => {
      // Request persistent storage so the browser does not silently evict
      // this IndexedDB under storage pressure (best-effort bucket).
      if (navigator.storage && navigator.storage.persist) {
        navigator.storage.persist().catch(() => {});
      }
      resolve(event.target.result);
    };
    request.onerror = (event) => {
      console.error('IndexedDB open error:', event.target.error);
      dbPromise = null;
      reject(event.target.error);
    };
  });

  return dbPromise;
}

/**
 * Get a cached document blob by document ID.
 * @param {string} docId
 * @returns {Promise<{blob: Blob, version: string|number, cachedAt: number}|null>}
 */
export async function getCachedDocument(docId) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(String(docId));
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({ blob: result.blob, version: result.version, cachedAt: result.cachedAt });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    console.warn('documentCache getCachedDocument failed:', err);
    return null;
  }
}

/**
 * Store or update a document blob in the cache.
 * Evicts oldest entries if total cache size exceeds MAX_CACHE_SIZE.
 * @param {string} docId
 * @param {Blob} blob
 * @param {string|number} version
 */
export async function cacheDocument(docId, blob, version) {
  try {
    const db = await openDB();

    // Write the entry
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put({ docId: String(docId), blob, version, cachedAt: Date.now(), size: blob.size });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });

    // Evict oldest if over size limit
    await evictIfNeeded(db);
  } catch (err) {
    console.warn('documentCache cacheDocument failed:', err);
  }
}

/**
 * Remove a cached document by ID.
 * @param {string} docId
 */
export async function invalidateDocument(docId) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.delete(String(docId));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn('documentCache invalidateDocument failed:', err);
  }
}

/**
 * Clear the entire document cache.
 */
export async function invalidateAll() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn('documentCache invalidateAll failed:', err);
  }
}

/**
 * Get approximate total size of all cached documents in bytes.
 * @returns {Promise<number>}
 */
export async function getCacheSize() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();
      request.onsuccess = () => {
        const total = (request.result || []).reduce((sum, entry) => sum + (entry.size || 0), 0);
        resolve(total);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    console.warn('documentCache getCacheSize failed:', err);
    return 0;
  }
}

/**
 * Evicts oldest cache entries until total size is under MAX_CACHE_SIZE.
 */
async function evictIfNeeded(db) {
  const entries = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });

  const totalSize = entries.reduce((sum, e) => sum + (e.size || 0), 0);
  if (totalSize <= MAX_CACHE_SIZE) return;

  // Sort oldest first
  entries.sort((a, b) => a.cachedAt - b.cachedAt);

  let sizeToFree = totalSize - MAX_CACHE_SIZE;
  const toDelete = [];
  for (const entry of entries) {
    if (sizeToFree <= 0) break;
    toDelete.push(entry.docId);
    sizeToFree -= entry.size || 0;
  }

  if (toDelete.length === 0) return;

  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    for (const id of toDelete) {
      store.delete(id);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
